import { useCallback, useEffect, useRef, useState } from "react";
import {
  listUsableAudioInputs,
  resolveSelectedAudioDeviceId,
  type AudioInputDevice,
  type VoicePermissionState,
} from "./voiceTrace";
import { ownerFacingGlobalError } from "../ownerFacingGlobalError";

interface RecorderState {
  available: boolean;
  recording: boolean;
  processing: boolean;
  error: string | null;
  permission: VoicePermissionState;
  devices: AudioInputDevice[];
  selectedDeviceId: string;
  lastTrace: string | null;
}

export type TtsVoice = "piper" | "brian" | "daniel" | "adam";
export const DEFAULT_TTS_VOICE: TtsVoice = "brian";
export interface TtsSpeakResult {
  ok: boolean;
  error?: string;
}

export interface ServerVoiceControls extends RecorderState {
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<string | null>;
  cancelRecording: () => void;
  speak: (text: string, lang?: "de" | "en", voice?: TtsVoice) => Promise<TtsSpeakResult>;
  cancelSpeech: () => void;
  refreshDevices: () => Promise<void>;
  requestPermission: () => Promise<void>;
  setSelectedDeviceId: (deviceId: string) => void;
  speaking: boolean;
  audioStream: MediaStream | null;
}

export const VOICE_STT_ENDPOINT = "/api/voice/stt";
export const VOICE_TTS_ENDPOINT = "/api/voice/tts";

export function voiceRequestHeaders(base: Record<string, string> = {}): Record<string, string> {
  return { ...base };
}

function parseVoiceErrorCode(bodyText: string): string | null {
  try {
    const parsed = JSON.parse(bodyText) as { error?: unknown; code?: unknown };
    const code = parsed.error ?? parsed.code;
    return typeof code === "string" ? code : null;
  } catch {
    return null;
  }
}

function parseVoiceRequestId(bodyText: string): string | null {
  try {
    const parsed = JSON.parse(bodyText) as { requestId?: unknown };
    return typeof parsed.requestId === "string" ? parsed.requestId : null;
  } catch {
    return null;
  }
}

function voiceOwnerErrorText(kind: "STT" | "TTS", bodyText: string): string {
  if (kind === "TTS") return ownerFacingGlobalError(`TTS: ${bodyText}`);
  try {
    const parsed = JSON.parse(bodyText) as { error?: unknown; message?: unknown };
    const detail = parsed.message ?? parsed.error;
    if (typeof detail === "string") return ownerFacingGlobalError(detail);
  } catch {
    /* fall through to raw owner-facing mapper */
  }
  return ownerFacingGlobalError(bodyText);
}

export function voiceEndpointErrorMessage(
  kind: "STT" | "TTS",
  endpoint: string,
  status: number,
  statusText: string,
  bodyText: string,
): string {
  const code = parseVoiceErrorCode(bodyText);
  const requestId = parseVoiceRequestId(bodyText);
  const prefix =
    status === 401 && (code === "missing_session" || code === "invalid_session")
      ? `Owner-Session fehlt oder ist abgelaufen. Bitte neu anmelden und Voice danach erneut starten. Endpoint: ${endpoint}.`
      : status === 401 && code === "unauthorized"
        ? `API-Zugriff nicht freigegeben. Der Server hat Voice vor der Owner-Session-Pruefung abgelehnt. Endpoint: ${endpoint}.`
        : status === 403
          ? `Owner-Freigabe erforderlich. Deine Session ist vorhanden, aber Voice braucht Owner/Admin-Rechte. Endpoint: ${endpoint}.`
          : null;
  if (prefix) return requestId ? `${prefix} Request-ID ${requestId}` : prefix;

  const body = bodyText.trim() || `${status} ${statusText || "Fehler"}`;
  const ownerMessage = voiceOwnerErrorText(kind, body);
  return [
    `${kind} konnte nicht verarbeitet werden. ${ownerMessage}`,
    `Endpoint: ${endpoint}.`,
    requestId ? `Request-ID ${requestId}` : null,
  ]
    .filter(Boolean)
    .join(" ");
}

/**
 * Server-getriebene Voice-Pipeline:
 *  - Aufnahme via MediaRecorder → multipart POST /api/voice/stt
 *  - TTS via POST /api/voice/tts → audio/wav-Stream → HTMLAudioElement,
 *    captureStream() → exponiert MediaStream für Orb-Audio-Reaktivität
 */
export function useServerVoice(): ServerVoiceControls {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const sttAbortRef = useRef<AbortController | null>(null);
  const ttsAbortRef = useRef<AbortController | null>(null);
  const startRecordingPromiseRef = useRef<Promise<void> | null>(null);
  const recordingStartTokenRef = useRef(0);
  const chunksRef = useRef<Blob[]>([]);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<RecorderState>({
    available: typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia,
    recording: false,
    processing: false,
    error: null,
    permission: "unknown",
    devices: [],
    selectedDeviceId: "",
    lastTrace: "init",
  });
  const [speaking, setSpeaking] = useState(false);

  const refreshDevices = useCallback(async (): Promise<void> => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) {
      setState((s) => ({ ...s, lastTrace: "enumerateDevices-unavailable" }));
      return;
    }

    try {
      const devices = listUsableAudioInputs(await navigator.mediaDevices.enumerateDevices());
      setState((s) => ({
        ...s,
        devices,
        selectedDeviceId: resolveSelectedAudioDeviceId(devices, s.selectedDeviceId),
        lastTrace: devices.length > 0 ? `devices:${devices.length}` : "no-audioinput-devices",
      }));
    } catch (e) {
      setState((s) => ({
        ...s,
        error: e instanceof Error ? e.message : String(e),
        lastTrace: "enumerateDevices-error",
      }));
    }
  }, []);

  const refreshPermission = useCallback(async (): Promise<void> => {
    if (typeof navigator === "undefined" || !navigator.permissions?.query) {
      setState((s) => ({ ...s, permission: "unsupported", lastTrace: "permission-query-unsupported" }));
      return;
    }

    try {
      const status = await navigator.permissions.query({ name: "microphone" as PermissionName });
      setState((s) => ({ ...s, permission: status.state, lastTrace: `permission:${status.state}` }));
      status.onchange = () => {
        setState((s) => ({ ...s, permission: status.state, lastTrace: `permission:${status.state}` }));
      };
    } catch {
      setState((s) => ({ ...s, permission: "unsupported", lastTrace: "permission-query-unsupported" }));
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<void> => {
    if (!state.available) {
      setState((s) => ({ ...s, error: "Mikrofon-API nicht verfügbar", lastTrace: "mic-api-unavailable" }));
      return;
    }

    try {
      setState((s) => ({ ...s, error: null, lastTrace: "permission-request-start" }));
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setState((s) => ({ ...s, permission: "granted", lastTrace: "permission-granted" }));
      await refreshDevices();
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setState((s) => ({
        ...s,
        recording: false,
        processing: false,
        permission: message.toLowerCase().includes("denied") ? "denied" : s.permission,
        error: message,
        lastTrace: "permission-request-error",
      }));
    }
  }, [refreshDevices, state.available]);

  // Lazy-init des Audio-Elements für TTS-Playback. captureStream() erlaubt
  // den Web-Audio-API-Anschluss für die Orb-Reaktivität.
  const ensureAudio = useCallback((): HTMLAudioElement => {
    if (!audioElRef.current) {
      const a = new Audio();
      a.preload = "auto";
      a.crossOrigin = "anonymous";
      audioElRef.current = a;
    }
    return audioElRef.current;
  }, []);

  const startRecording = useCallback(async (): Promise<void> => {
    if (startRecordingPromiseRef.current) {
      return startRecordingPromiseRef.current;
    }
    if (!state.available) {
      setState((s) => ({ ...s, error: "Mikrofon-Zugriff nicht möglich" }));
      return;
    }
    const startToken = ++recordingStartTokenRef.current;
    const run = (async (): Promise<void> => {
      try {
        sttAbortRef.current?.abort();
        ttsAbortRef.current?.abort();
        const audio =
          state.selectedDeviceId.length > 0
            ? { deviceId: { exact: state.selectedDeviceId } }
            : true;
        setState((s) => ({ ...s, error: null, lastTrace: "recording-starting" }));
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio });
        } catch (e) {
          if (state.selectedDeviceId.length === 0) throw e;
          setState((s) => ({
            ...s,
            selectedDeviceId: "",
            lastTrace: "selected-device-missing:fallback-default",
          }));
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
        if (startToken !== recordingStartTokenRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          setState((s) => ({ ...s, recording: false, processing: false, lastTrace: "recording-cancelled" }));
          return;
        }
        mediaStreamRef.current = stream;
        chunksRef.current = [];
        const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : MediaRecorder.isTypeSupported("audio/webm")
            ? "audio/webm"
            : "";
        const rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
        rec.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        rec.onstop = () => {
          mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
          mediaStreamRef.current = null;
        };
        mediaRecorderRef.current = rec;
        rec.start();
        setState((s) => ({
          ...s,
          available: true,
          recording: true,
          processing: false,
          permission: "granted",
          error: null,
          lastTrace: "recording-started",
        }));
        void refreshDevices();
      } catch (e) {
        setState((s) => ({
          ...s,
          recording: false,
          processing: false,
          error: e instanceof Error ? e.message : String(e),
          lastTrace: "recording-error",
        }));
      }
    })();
    startRecordingPromiseRef.current = run;
    try {
      await run;
    } finally {
      if (startRecordingPromiseRef.current === run) {
        startRecordingPromiseRef.current = null;
      }
    }
  }, [refreshDevices, state.available, state.selectedDeviceId]);

  const cancelRecording = useCallback((): void => {
    recordingStartTokenRef.current++;
    const hadActiveVoiceWork =
      !!mediaRecorderRef.current ||
      !!mediaStreamRef.current ||
      !!startRecordingPromiseRef.current ||
      !!sttAbortRef.current ||
      chunksRef.current.length > 0;

    if (!hadActiveVoiceWork) {
      return;
    }

    sttAbortRef.current?.abort();
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      rec.stop();
    }
    mediaRecorderRef.current = null;
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
    chunksRef.current = [];
    setState((s) => ({ ...s, recording: false, processing: false, lastTrace: "recording-cancelled" }));
  }, []);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    if (startRecordingPromiseRef.current) {
      await startRecordingPromiseRef.current;
    }
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === "inactive") return null;
    await new Promise<void>((resolve) => {
      rec.addEventListener("stop", () => resolve(), { once: true });
      rec.stop();
    });
    mediaRecorderRef.current = null;
    setState((s) => ({ ...s, recording: false, processing: true, lastTrace: "stt-upload-starting" }));
    const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
    chunksRef.current = [];
    if (blob.size === 0) {
      setState((s) => ({ ...s, processing: false, lastTrace: "empty-recording" }));
      return null;
    }

    try {
      const controller = new AbortController();
      sttAbortRef.current = controller;
      const form = new FormData();
      form.append("audio", blob, "recording.webm");
      const res = await fetch(VOICE_STT_ENDPOINT, {
        method: "POST",
        headers: voiceRequestHeaders(),
        body: form,
        credentials: "same-origin",
        signal: controller.signal,
      });
      const bodyText = await res.text();
      let data: { ok?: boolean; text?: string; error?: string } = {};
      try {
        data = bodyText ? (JSON.parse(bodyText) as { ok?: boolean; text?: string; error?: string }) : {};
      } catch {
        data = {};
      }
      if (!res.ok || !data.ok) {
        const error = voiceEndpointErrorMessage("STT", VOICE_STT_ENDPOINT, res.status, res.statusText, bodyText);
        setState((s) => ({ ...s, processing: false, error, lastTrace: "stt-error" }));
        return null;
      }
      setState((s) => ({ ...s, processing: false, lastTrace: `stt-ok:${blob.size}b` }));
      void refreshDevices();
      return data.text ?? "";
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") return null;
      setState((s) => ({
        ...s,
        processing: false,
        error: e instanceof Error ? e.message : String(e),
        lastTrace: "stt-exception",
      }));
      return null;
    } finally {
      sttAbortRef.current = null;
    }
  }, [refreshDevices]);

  const cancelSpeech = useCallback((): void => {
    ttsAbortRef.current?.abort();
    const a = audioElRef.current;
    if (a) {
      a.pause();
      a.currentTime = 0;
    }
    setSpeaking(false);
  }, []);

  const speak = useCallback(
    async (text: string, lang: "de" | "en" = "de", voice: TtsVoice = DEFAULT_TTS_VOICE): Promise<TtsSpeakResult> => {
      if (!text.trim()) return { ok: false, error: "TTS: leerer Text" };
      try {
        cancelSpeech();
        const controller = new AbortController();
        ttsAbortRef.current = controller;
        const res = await fetch(VOICE_TTS_ENDPOINT, {
          method: "POST",
          headers: voiceRequestHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ text, lang, voice }),
          credentials: "same-origin",
          signal: controller.signal,
        });
        if (!res.ok) {
          const errText = await res.text();
          const error = voiceEndpointErrorMessage("TTS", VOICE_TTS_ENDPOINT, res.status, res.statusText, errText.slice(0, 200));
          setState((s) => ({ ...s, error }));
          return { ok: false, error };
        }
        const blob = await res.blob();
        // Leere/ungültige Audio-Antwort gar nicht erst abspielen (konsistent
        // zum STT-Pfad). Verhindert, dass ein 0-Byte-Blob ins Audio-Element
        // gehängt und als Fehler abgespielt wird.
        if (blob.size === 0) {
          setState((s) => ({ ...s, error: "TTS: leere Audio-Antwort" }));
          return { ok: false, error: "TTS: leere Audio-Antwort" };
        }
        const url = URL.createObjectURL(blob);
        const a = ensureAudio();
        a.src = url;
        // captureStream() bewusst nicht genutzt — provoziert WebGL-Context-Loss
        // in Firefox/Chrome wenn parallel ein WebGL-Canvas läuft. Audio-Reaktivität
        // kommt zurück über Web-Audio-Analyser direkt am Mic-Pfad (Phase 2 Polish).
        a.onplay = () => setSpeaking(true);
        const playbackDone = new Promise<void>((resolve) => {
          let done = false;
          const cleanup = (): void => {
            a.onended = null;
            a.onerror = null;
            controller.signal.removeEventListener("abort", onAbort);
          };
          const finish = (): void => {
            if (done) return;
            done = true;
            cleanup();
            setSpeaking(false);
            // Audio-Element VOR dem Revoke von der Blob-URL lösen. Sonst behält
            // das wiederverwendete Element die gleich tote URL als src; der
            // nächste Turn triggert über cancelSpeech() (pause()/currentTime=0)
            // einen Media-Reload und der Browser requestet die bereits revokte
            // Blob-URL → net::ERR_FILE_NOT_FOUND pro Turn (Audio läuft, aber
            // kosmetischer Console-Noise). cleanup() hat onerror bereits
            // genullt, daher feuert load() keinen Handler.
            a.removeAttribute("src");
            a.load();
            URL.revokeObjectURL(url);
            if (ttsAbortRef.current === controller) {
              ttsAbortRef.current = null;
            }
            resolve();
          };
          const onAbort = (): void => finish();
          a.onended = finish;
          a.onerror = finish;
          controller.signal.addEventListener("abort", onAbort, { once: true });
        });
        await a.play();
        await playbackDone;
        return { ok: true };
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return { ok: false, error: "TTS: abgebrochen" };
        const error = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error }));
        return { ok: false, error };
      } finally {
        if (ttsAbortRef.current?.signal.aborted) {
          ttsAbortRef.current = null;
        }
      }
    },
    [cancelSpeech, ensureAudio],
  );

  useEffect(
    () => () => {
      const rec = mediaRecorderRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
      sttAbortRef.current?.abort();
      ttsAbortRef.current?.abort();
      mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
      const a = audioElRef.current;
      if (a) {
        a.pause();
        a.src = "";
      }
    },
    [],
  );

  useEffect(() => {
    void refreshPermission();
    void refreshDevices();
    navigator.mediaDevices?.addEventListener?.("devicechange", refreshDevices);
    return () => navigator.mediaDevices?.removeEventListener?.("devicechange", refreshDevices);
  }, [refreshDevices, refreshPermission]);

  return {
    ...state,
    startRecording,
    stopRecording,
    cancelRecording,
    speak,
    cancelSpeech,
    refreshDevices,
    requestPermission,
    setSelectedDeviceId: (deviceId: string) =>
      setState((s) => ({ ...s, selectedDeviceId: deviceId, lastTrace: deviceId ? "device-selected:custom" : "device-selected:default" })),
    speaking,
    audioStream: mediaStreamRef.current,
  };
}
